import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, RefreshCw, WifiOff } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { usePortalDestination } from '../PortalDestinationContext';
import { OTCOrders } from '../shop/OTCOrders';
import { PortalEmptyState } from '../ui/PortalEmptyState';
import { errorText } from '../health/healthConfig';

export default function PortalOrdersPage() {
  const { api: portalApi, href, branch } = usePortalDestination();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const navigate = useNavigate();

  const load = () => {
    setLoading(true); setLoadError('');
    portalApi.get('/orders')
      .then((r) => setRows(r.data.orders || []))
      .catch((e) => setLoadError(errorText(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, [portalApi]);

  const reorder = (order) => {
    const items = (order.items || [])
      .map((i) => ({
        product_name: i.product_name || i.name || '',
        quantity: Number(i.quantity) || 1,
      }))
      .filter((i) => i.product_name);
    if (items.length === 0) return;
    navigate(href('refills'), {
      state: { prefill: { items, source_sale_id: order.sale_id } },
    });
  };

  return (
    <div className="space-y-5" data-testid="portal-orders-page">
      <h1 className="text-2xl font-bold text-slate-900">Order history</h1>
      <OTCOrders />
      <Card>
        <CardHeader><CardTitle>Past purchases</CardTitle></CardHeader>
        <CardContent>
          {loading && <div className="text-sm text-slate-400 py-4" data-testid="portal-orders-loading">Loading…</div>}
          {!loading && loadError && (
            <PortalEmptyState icon={WifiOff} title="Couldn't load your order history" description={loadError}
              testId="portal-orders-load-error"
              action={<Button variant="outline" size="sm" onClick={load} data-testid="portal-orders-retry">Try again</Button>} />
          )}
          {!loading && !loadError && rows.length === 0 && (
            <div className="text-sm text-slate-400 py-6 text-center">
              No orders yet. Once you buy from a DutiLyne pharmacy that has your phone number, your history will appear here.
            </div>
          )}
          <div className="space-y-2">
            {rows.map((o) => {
              const itemCount = (o.items || []).length;
              return (
                <div key={o.sale_id} className="border rounded-xl p-3 flex gap-3 items-start" data-testid={`portal-order-${o.sale_id}`}>
                  <div className="p-2 rounded-lg bg-emerald-100 text-emerald-700"><ShoppingBag className="w-4 h-4" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start gap-2">
                      <div className="font-medium">Order · GHS {Number(o.total || 0).toFixed(2)}</div>
                      <Badge variant="outline">{o.payment_method}</Badge>
                    </div>
                    <div className="text-xs text-slate-500">{new Date(o.created_at).toLocaleString()}</div>
                    <div className="text-xs text-slate-600" data-testid={`portal-order-location-${o.sale_id}`}>{o.branch_name}</div>
                    <div className="mt-1 text-sm text-slate-700">
                      {(o.items || []).slice(0, 3).map((i) => i.product_name).join(', ') || 'Items'}
                      {itemCount > 3 && <> +{itemCount - 3} more</>}
                    </div>
                    {itemCount > 0 && (
                      <Button
                        size="sm" variant="ghost"
                        disabled={!branch || branch.tenant_id !== o.tenant_id}
                        title={!branch || branch.tenant_id !== o.tenant_id ? 'Select the original order branch to reorder' : 'Request these items again'}
                        onClick={() => reorder(o)}
                        data-testid={`portal-order-reorder-${o.sale_id}`}
                        className="mt-2 text-emerald-700 hover:bg-emerald-50 h-8 px-2"
                      >
                        <RefreshCw className="w-3.5 h-3.5 mr-1" /> Reorder these items
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
